import React, { useMemo, useState, lazy, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { classNames } from '../../../utils/styleHelpers';
import useLayout from '../../../hooks/useLayout';

const Header = lazy(() => import('../Header/Header'));
const MobileSideBar = lazy(() => import('../MobileSideBar/MobileSideBar'));
const SideBar = lazy(() => import('../SideBar/SideBar'));

const SIDEBAR_COLLAPSED_WIDTH = 70;
const SIDEBAR_EXPANDED_WIDTH = 260;
const SIDEBAR_STATE_KEY = 'sidebar-expanded';

const MainLayout = () => {
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
    const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(() => {
        // Initialize from localStorage, default to false (collapsed)
        const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
        return saved === 'true';
    });

    const location = useLocation();
    const { hideDefaultHeader } = useLayout();

    const showSideBar: boolean = useMemo(() => {
        return !location.pathname.includes('/knowledge/content');
    }, [location]);

    // Persist sidebar state to localStorage
    useEffect(() => {
        localStorage.setItem(SIDEBAR_STATE_KEY, String(sidebarExpanded));
    }, [sidebarExpanded]);

    const toggleSidebar = () => {
        setSidebarExpanded(prev => !prev);
    };

    const sidebarWidth = sidebarExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

    return (
        <>
            <Suspense fallback={<></>}>
                <MobileSideBar isMobileSidebarOpen={isMobileSidebarOpen} setIsMobileSidebarOpen={setIsMobileSidebarOpen} />
            </Suspense>
            <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex">
                {showSideBar &&
                    <Suspense fallback={<></>}>
                        <SideBar expanded={sidebarExpanded} onToggle={toggleSidebar} />
                    </Suspense>
                }
            </div>
            <div 
                className={classNames('h-screen flex flex-col transition-[padding] duration-300')}
                style={{ paddingLeft: showSideBar ? `${sidebarWidth}px` : '0px' }}
            >
                {!hideDefaultHeader &&
                    <Suspense fallback={<></>}>
                        <Header setIsMobileSidebarOpen={setIsMobileSidebarOpen} />
                    </Suspense>
                }
                <Outlet />
            </div>
        </>
    )
}

export default MainLayout;
